import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material'
import {
  Download,
  PictureAsPdf
} from '@mui/icons-material'

const formatFileSize = (base64String) => {
  if (!base64String) return '0 KB'

  // Calculate approximate file size from base64 string
  const sizeInBytes = (base64String.length * 3) / 4

  if (sizeInBytes < 1024) {
    return `${sizeInBytes.toFixed(0)} B`
  } else if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(2)} KB`
  } else {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`
  }
}

const AttachmentItem = ({ attachment }) => {
  const handleDownload = useCallback(() => {
    if (!attachment?.fileBytes || !attachment?.fileName) return

    try {
      // Create a blob from base64 string
      const byteCharacters = atob(attachment.fileBytes)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: attachment.contentType || 'application/octet-stream' })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = attachment.fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading file:', error)
    }
  }, [attachment])

  return (
    <ListItem
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        py: 0.75,
        minHeight: 'auto',
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
    >
      <PictureAsPdf sx={{ mr: 1.5, color: 'primary.main', fontSize: '1.25rem' }} />
      <ListItemText
        primary={attachment.fileName}
        secondary={formatFileSize(attachment.fileBytes)}
        primaryTypographyProps={{
          sx: { fontWeight: 500, fontSize: '0.875rem' }
        }}
        secondaryTypographyProps={{
          sx: { fontSize: '0.75rem' }
        }}
      />
      <ListItemSecondaryAction>
        <IconButton
          edge="end"
          aria-label="download"
          onClick={handleDownload}
          size="small"
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.lighter'
            }
          }}
        >
          <Download fontSize="small" />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  )
}

const CampaignAttachments = ({ attachments }) => {
  const { t } = useTranslation()

  if (!attachments || attachments.length === 0) {
    return null
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 3,
          color: 'primary.main',
          fontSize: '1.1rem'
        }}
      >
        {t('mailTemplate.campaigns.detail.attachments')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
        {attachments.map((attachment, index) => (
          <Box key={index} sx={{ flex: '1 1 300px', maxWidth: 400, minWidth: 250 }}>
            <AttachmentItem attachment={attachment} />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default CampaignAttachments
